import React, { useContext, useState, useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import {
  Typography,
  Paper,
  Button,
  Autocomplete,
  TextField,
  Container,
} from "@mui/material";
import { BarLoader } from "react-spinners";
import ButtonNative from "../StyledComponents/ButtonNative";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import OCRContext from "../../context/ocr-context";
import UserContext from "../../context/user-context";
import { useStyles } from "./styles";
import httpRequest from "../../httpRequest";
import { PDFDocument } from "pdf-lib";
import { ReactSortable } from "react-sortablejs";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.js`;

const UploadCard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const ocrCtx = useContext(OCRContext);
  const userCtx = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const [pages, setPages] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedFunction, setSelectedFunction] = useState("");
  const [companyError, setCompanyError] = useState(false);
  const [functionError, setFunctionError] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const didMount = useRef(false);

  const { employeeData } = userCtx;
  const { company_name, function_name, companies = [], functions = [] } = employeeData || {};

  useEffect(() => {
    if (didMount.current) return;
    didMount.current = true;

    ocrCtx.resetOCRState();

    const fetchData = async () => {
      try {
        const vendorsResp = await httpRequest.get(
          `${process.env.REACT_APP_BACKEND_URL}/get-vendors`
        );
        setVendors(vendorsResp.data.vendors);

        setSelectedCompany(company_name);
        ocrCtx.setCompanyId(employeeData.company_id);

        setSelectedFunction(function_name);
        ocrCtx.setFunctionId(employeeData.function_id);

        const response = await httpRequest.get(
          `${process.env.REACT_APP_BACKEND_URL}/get-companies?id=${employeeData.company_id}`
        );
        let companies = Array.isArray(response.data.companies)
          ? response.data.companies
          : [response.data.company];

        if (companies.length > 0) {
          const balance = companies[0].balance;
          if (balance === 0) {
            enqueueSnackbar("Your credits have finished. Please contact support.", {
              variant: "warning",
            });
            setUploadDisabled(true);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [userCtx, employeeData]);

  const generateThumbnails = async (file, pageIdPrefix, startPageNum) => {
    const thumbnails = [];
    if (file.type === "application/pdf") {
      const buffer = await file.arrayBuffer();
      const pdfjsBuffer = buffer.slice(0);
      const pdfLibBuffer = buffer.slice(0);

      const pdfDoc = await pdfjsLib.getDocument(pdfjsBuffer).promise;
      const sourceDoc = await PDFDocument.load(pdfLibBuffer);

      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        const viewport = page.getViewport({ scale: 1 });
        const scale = (600 / viewport.width) * window.devicePixelRatio;
        const scaledViewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;
        const context = canvas.getContext("2d");
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = "high";

        await page.render({
          canvasContext: context,
          viewport: scaledViewport,
        }).promise;

        thumbnails.push({
          id: `${pageIdPrefix}-${i}`,
          thumbnail: canvas.toDataURL("image/png", 1.0),
          pageNum: startPageNum + i - 1,
          originalFile: file,
          isPdf: true,
          filePageNum: i,
        });
      }
      return thumbnails;
    } else {
      const img = new Image();
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      img.src = URL.createObjectURL(file);
      await new Promise((resolve) => (img.onload = resolve));

      canvas.width = img.width;
      canvas.height = img.height;
      context.drawImage(img, 0, 0);

      thumbnails.push({
        id: `${pageIdPrefix}-1`,
        thumbnail: canvas.toDataURL("image/png", 1.0),
        pageNum: startPageNum,
        originalFile: file,
        isPdf: false,
        filePageNum: 1,
      });
      return thumbnails;
    }
  };

  const handleFileUpload = async (event) => {
    setIsLoading(true);
    const newFiles = Array.from(event.target.files);

    try {
      let currentPageNum = pages.length;
      const newPages = await Promise.all(
        newFiles.map(async (file, index) => {
          const pageIdPrefix = `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`;
          const thumbnails = await generateThumbnails(file, pageIdPrefix, currentPageNum + 1);
          currentPageNum += thumbnails.length;
          return thumbnails;
        })
      );
      setPages((prev) => [...prev, ...newPages.flat()]);
    } catch (error) {
      console.error("Error processing files:", error);
      enqueueSnackbar("Error processing files", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageDelete = (pageId) => {
    setPages((prev) => {
      const newPages = prev.filter((page) => page.id !== pageId);
      return newPages.map((page, index) => ({
        ...page,
        pageNum: index + 1,
      }));
    });
  };

  const compileToPdf = async () => {
    const pdfDoc = await PDFDocument.create();
    for (const page of pages) {
      if (page.isPdf) {
        const buffer = await page.originalFile.arrayBuffer();
        const sourceDoc = await PDFDocument.load(buffer.slice(0));
        const [copiedPage] = await pdfDoc.copyPages(sourceDoc, [page.filePageNum - 1]);
        pdfDoc.addPage(copiedPage);
      } else {
        const img = new Image();
        img.src = page.thumbnail;
        await new Promise((resolve) => (img.onload = resolve));
        const pdfPage = pdfDoc.addPage([img.width, img.height]);
        const pngImage = await pdfDoc.embedPng(page.thumbnail);
        pdfPage.drawImage(pngImage, {
          x: 0,
          y: 0,
          width: img.width,
          height: img.height,
        });
      }
    }
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    return new File([blob], "compiled.pdf", { type: "application/pdf" });
  };

  const handleVendorChange = (event, value) => {
    setSelectedVendor(value);
    const selectedVendorData = vendors.find(
      (vendor) => vendor.vendor_name === value
    );
    if (selectedVendorData) {
      ocrCtx.setVendorCode(selectedVendorData.vendor_code);
    }
  };

  const handleCompanyChange = async (event, value) => {
    setSelectedCompany(value ? value.company_name : "");
    setCompanyError(false);

    const selectedCompanyData = companies.find(
      (company) => company.company_name === value?.company_name
    );
    if (selectedCompanyData) {
      ocrCtx.setCompanyId(selectedCompanyData.id);
      try {
        const response = await httpRequest.get(
          `${process.env.REACT_APP_BACKEND_URL}/get-companies?id=${selectedCompanyData.id}`
        );
        const balance = response.data.companies[0].balance;
        if (balance === 0) {
          enqueueSnackbar("Credits finished", { variant: "error" });
          setUploadDisabled(true);
        } else {
          setUploadDisabled(false);
        }
      } catch (error) {
        console.error("Error fetching balance:", error);
      }
    } else {
      ocrCtx.setCompanyId(null);
      setUploadDisabled(false);
    }
  };

  const handleFunctionChange = (event, value) => {
    setSelectedFunction(value ? value.function_name : "");
    setFunctionError(false);

    const selectedFunctionData = functions.find(
      (func) => func.function_name === value?.function_name
    );
    if (selectedFunctionData) {
      ocrCtx.setFunctionId(selectedFunctionData.id);
    } else {
      ocrCtx.setFunctionId(null);
    }
  };

  const openCamera = () => {
    const inputElement = document.createElement("input");
    inputElement.setAttribute("type", "file");
    inputElement.setAttribute("accept", "image/*");
    inputElement.setAttribute("capture", "camera");
    inputElement.addEventListener("change", handleFileUpload, false);
    inputElement.click();
  };

  UploadCard.openCamera = openCamera;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        borderRadius: 4,
        backdropFilter: "blur(10px)",
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        margin: "0 auto",
        marginTop: 20,
        width: "90%",
        maxWidth: "1200px",
      }}
    >
      <Typography
        variant="h5"
        style={{
          color: "#354a5f",
          fontFamily: "SapFiori72, sans-serif",
          marginBottom: 4,
        }}
      >
        Upload Invoice
      </Typography>
      <div className={classes.input}>
        <ButtonOutlined
          style={{ marginRight: "20px", padding: "5px 35px" }}
          onClick={openCamera}
        >
          SCAN
        </ButtonOutlined>
        <ButtonOutlined
          style={{ padding: "5px 35px" }}
          onClick={() => document.getElementById("upload-card-file-input").click()}
        >
          UPLOAD
        </ButtonOutlined>
        <input
          type="file"
          id="upload-card-file-input"
          className="form-control"
          style={{ display: "none" }}
          multiple
          accept="application/pdf,image/*"
          onChange={handleFileUpload}
        />
      </div>
      {pages.length > 0 && (
        <Container maxWidth="xl" sx={{ textAlign: "center", padding: "20px" }}>
          <div className={classes.vendorSelect}>
            {companies.length > 1 && (
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => option.company_name || ""}
                onChange={handleCompanyChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Company"
                    variant="outlined"
                    error={companyError}
                  />
                )}
                value={
                  companies.find(
                    (company) => company.company_name === selectedCompany
                  ) || null
                }
              />
            )}
            {functions.length > 1 && (
              <Autocomplete
                options={functions}
                getOptionLabel={(option) => option.function_name || ""}
                onChange={handleFunctionChange}
                sx={{ marginTop: 2 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Function"
                    variant="outlined"
                    error={functionError}
                  />
                )}
                value={
                  functions.find(
                    (func) => func.function_name === selectedFunction
                  ) || null
                }
              />
            )}
            <Autocomplete
              value={selectedVendor}
              onChange={handleVendorChange}
              options={vendors.map((vendor) => vendor.vendor_name)}
              sx={{ marginTop: 2, marginBottom: 2 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Vendor"
                  variant="outlined"
                />
              )}
            />
          </div>

          <ReactSortable
            list={pages}
            setList={setPages}
            animation={200}
            style={{
              display: "flex",
              gap: "20px",
              overflowX: "auto",
              padding: "20px",
              border: "1px solid #ccc",
              minHeight: "400px",
              justifyContent: "center", // Center thumbnails
              flexWrap: "wrap", // Allow wrapping for better single-page centering
            }}
          >
            {pages.map((page, index) => (
              <div
                key={page.id}
                style={{
                  position: "relative",
                  cursor: "move",
                  border: "1px solid #ddd",
                  padding: "5px",
                  background: "#f9f9f9",
                  width: "220px", // Fixed width
                  maxWidth: "220px", // Prevent expansion
                  boxSizing: "border-box",
                }}
              >
                <img
                  src={page.thumbnail}
                  alt={`Page ${index + 1}`}
                  style={{
                    width: "100%", // Fill parent width
                    maxWidth: "210px", // Slightly less than parent to account for padding
                    maxHeight: "300px", // Cap height for consistency
                    height: "auto",
                    cursor: "pointer",
                    display: "block", // Remove default image spacing
                    margin: "0 auto", // Center image within div
                  }}
                  onClick={() => setSelectedPage(page)}
                />
                <div style={{ textAlign: "center" }}>
                  <small>Page {index + 1}</small>
                </div>
                <button
                  onClick={() => handlePageDelete(page.id)}
                  style={{
                    position: "absolute",
                    top: "5px",
                    right: "5px",
                    background: "#354a5f",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "20px",
                    height: "20px",
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </ReactSortable>
          
          <ButtonNative
            style={{
              margin: "20px",
              padding: "5px 35px",
            }}
            onClick={async () => {
              if (companies.length > 1 && !selectedCompany) {
                setCompanyError(true);
                return;
              }
              if (functions.length > 1 && !selectedFunction) {
                setFunctionError(true);
                return;
              }
              setIsLoading(true);
              const pdfFile = await compileToPdf();
              ocrCtx.setFile(pdfFile);
              ocrCtx.setActivePage(1);
              setIsLoading(false);
            }}
            disabled={uploadDisabled || pages.length === 0}
          >
            NEXT
          </ButtonNative>
        </Container>
      )}
      {isLoading && (
        <div className={classes.loader}>
          <BarLoader color="#0070f2" width={150} />
        </div>
      )}
      {selectedPage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setSelectedPage(null)}
        >
          <img
            src={selectedPage.thumbnail}
            alt={`Preview of Page ${selectedPage.pageNum}`}
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setSelectedPage(null)}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "#354a5f",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}
    </Paper>
  );
};

export default UploadCard;